import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { getHubErrorMessage } from "@memorang/helpers/api";
import { isAxiosError } from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { UpdateUserFields } from "../../../api/hub/user-service";

type SaveProfileResult = { ok: true } | { ok: false; error: string };

export const useUpdateUserProfile = () => {
	const { updateUser } = useAuthContext();
	const { t } = useTranslation();

	const saveProfile = useCallback(
		async (fields: UpdateUserFields): Promise<SaveProfileResult> => {
			try {
				await updateUser(fields);
				return { ok: true };
			} catch (error) {
				captureErrorToSentry(error, {
					context: "useUpdateUserProfile.saveProfile",
				});

				if (
					isAxiosError(error) &&
					(error.response?.status === 400 || error.response?.status === 409)
				) {
					const message = getHubErrorMessage(error.response.data);
					if (message) {
						return { ok: false, error: message };
					}
				}

				return {
					ok: false,
					error: t("onboarding.steps.profile.profileSaveFailed"),
				};
			}
		},
		[t, updateUser],
	);

	return { saveProfile };
};
